import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { useRef, useState } from "react";
import axiosInstance from "api/axiosInstance";
import { toast } from "react-toastify";

import usePayslipTableData from "./data/authorsTableData";

const COMPANY_NAME = "Quintus Tech Pvt Ltd";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatCurrency = (value) => {
  const amount = Number(value ?? 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN");
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getFixedSalaryRows = (payslip) => [
  ["Basic", payslip?.fixedSalary?.basic],
  ["HRA", payslip?.fixedSalary?.hra],
  ["Conveyance", payslip?.fixedSalary?.conveyance],
  ["Gross", payslip?.fixedSalary?.gross],
  ["Bonus", payslip?.fixedSalary?.bonus],
];

const getEarningRows = (payslip) => [
  ["Basic", payslip?.earnings?.basic],
  ["HRA", payslip?.earnings?.hra],
  ["Conveyance", payslip?.earnings?.conveyance],
  ["Gross", payslip?.earnings?.gross],
  ["Sunday Working Payout", payslip?.earnings?.sundayWorkingPayout],
  ["Stat Bonus", payslip?.earnings?.statBonus],
  ["PL Encashment", payslip?.earnings?.plEncashment],
  ["Previous Month Sunday Working", payslip?.earnings?.previousMonthSundayWorking],
  ["Anniversary Bonus / Previous Deducted Added", payslip?.earnings?.anniversaryBonusOrPrevDeductedAdded],
  ["Earned Incentive", payslip?.earnings?.earnedIncentive],
];

const getDeductionRows = (payslip) => [
  ["PF Employee", payslip?.deductions?.pfEmployee],
  ["PF Employer", payslip?.deductions?.pfEmployer],
  ["ESIC Employee", payslip?.deductions?.esicEmployee],
  ["ESIC Employer", payslip?.deductions?.esicEmployer],
  ["PT", payslip?.deductions?.pt],
  ["TDS", payslip?.deductions?.tds],
  ["LWF", payslip?.deductions?.lwf],
  ["Advance Deduction", payslip?.deductions?.advanceDeduction],
  ["Performance Deduction", payslip?.deductions?.performanceWiseDeductionAmount],
  ["Total Deduction", payslip?.deductions?.totalDeduction],
];

const buildPrintMarkup = (payslip) => {
  const fixedSalaryRows = getFixedSalaryRows(payslip)
    .map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(formatCurrency(value))}</td></tr>`)
    .join("");
  const earningRows = getEarningRows(payslip)
    .map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(formatCurrency(value))}</td></tr>`)
    .join("");
  const deductionRows = getDeductionRows(payslip)
    .map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(formatCurrency(value))}</td></tr>`)
    .join("");

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>${escapeHtml(`${payslip?.employee?.name || "employee"}_${payslip?.period?.label || "payslip"}`)}</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 12px; font-family: Arial, sans-serif; color: #1f2937; background: #f3f6fb; }
          .sheet { max-width: 980px; margin: 0 auto; background: #ffffff; border: 1px solid #d9e2f2; box-shadow: 0 12px 40px rgba(15, 23, 42, 0.08); }
          .header { padding: 18px 22px 14px; border-bottom: 2px solid #1976d2; background: linear-gradient(135deg, #eff6ff 0%, #ffffff 70%); }
          .company { font-size: 22px; font-weight: 700; color: #0f172a; }
          .subtitle { margin-top: 4px; color: #475569; font-size: 11px; letter-spacing: 0.8px; text-transform: uppercase; }
          .hero { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 10px; padding: 14px 22px 0; }
          .panel { border: 1px solid #dbe7f5; background: #f8fbff; border-radius: 10px; padding: 10px 12px; }
          .panel-title { font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; margin-bottom: 8px; }
          .info-row { display: grid; grid-template-columns: 96px 1fr; gap: 8px; margin-bottom: 5px; }
          .key { font-size: 10px; color: #64748b; }
          .val { font-size: 11px; font-weight: 600; color: #0f172a; }
          .ctc-strip { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; padding: 12px 22px 0; }
          .ctc-box { border: 1px solid #dbe7f5; background: #eef6ff; border-radius: 10px; padding: 10px; }
          .ctc-label { font-size: 10px; text-transform: uppercase; color: #64748b; margin-bottom: 5px; }
          .ctc-value { font-size: 14px; font-weight: 700; color: #0f172a; }
          .section-wrap { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; padding: 14px 22px 0; }
          .section-title { font-size: 12px; font-weight: 700; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.8px; color: #0f172a; }
          table { width: 100%; border-collapse: collapse; border: 1px solid #dbe3f0; }
          th, td { padding: 6px 8px; border-bottom: 1px solid #e5edf7; font-size: 10px; line-height: 1.2; }
          th { background: #edf4ff; text-align: left; color: #1e3a8a; }
          td:last-child, th:last-child { text-align: right; }
          .summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; padding: 14px 22px 18px; }
          .summary-box { background: #eef6ff; color: #0f172a; border: 1px solid #d7e7ff; padding: 10px; border-radius: 10px; }
          .summary-box.dark { background: #0f172a; color: #ffffff; border: none; }
          .summary-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 5px; opacity: 0.85; }
          .summary-value { font-size: 14px; font-weight: 700; }
          .note { padding: 0 22px 16px; color: #64748b; font-size: 10px; }
          @page { size: A4 portrait; margin: 8mm; }
          @media print {
            body { background: #ffffff; padding: 0; }
            .sheet { border: none; box-shadow: none; width: 100%; }
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="header">
            <div class="company">${escapeHtml(COMPANY_NAME)}</div>
            <div class="subtitle">Salary Slip for ${escapeHtml(payslip?.period?.label || "-")}</div>
          </div>
          <div class="hero">
            <div class="panel">
              <div class="panel-title">Employee Details</div>
              <div class="info-row"><div class="key">Name</div><div class="val">${escapeHtml(payslip?.employee?.name || "-")}</div></div>
              <div class="info-row"><div class="key">Department</div><div class="val">${escapeHtml(payslip?.employee?.department || "-")}</div></div>
              <div class="info-row"><div class="key">Designation</div><div class="val">${escapeHtml(payslip?.employee?.designation || "-")}</div></div>
              <div class="info-row"><div class="key">Status</div><div class="val">${escapeHtml(payslip?.employee?.status || "-")}</div></div>
            </div>
            <div class="panel">
              <div class="panel-title">Period Details</div>
              <div class="info-row"><div class="key">Month</div><div class="val">${escapeHtml(payslip?.period?.month || "-")}</div></div>
              <div class="info-row"><div class="key">Year</div><div class="val">${escapeHtml(payslip?.period?.year || "-")}</div></div>
              <div class="info-row"><div class="key">Payslip Period</div><div class="val">${escapeHtml(payslip?.period?.label || "-")}</div></div>
              <div class="info-row"><div class="key">Employee ID</div><div class="val">${escapeHtml(payslip?.employee?.serialNo || "-")}</div></div>
              <div class="info-row"><div class="key">Date of Joining</div><div class="val">${escapeHtml(formatDate(payslip?.employee?.dateOfJoining))}</div></div>
            </div>
          </div>
          <div class="ctc-strip">
            <div class="ctc-box"><div class="ctc-label">Fixed CTC</div><div class="ctc-value">${escapeHtml(formatCurrency(payslip?.ctc?.fixedCTC))}</div></div>
            <div class="ctc-box"><div class="ctc-label">Incentive</div><div class="ctc-value">${escapeHtml(formatCurrency(payslip?.ctc?.incentive))}</div></div>
            <div class="ctc-box"><div class="ctc-label">Total CTC</div><div class="ctc-value">${escapeHtml(formatCurrency(payslip?.ctc?.totalCTC))}</div></div>
            <div class="ctc-box"><div class="ctc-label">Fixed Gross</div><div class="ctc-value">${escapeHtml(formatCurrency(payslip?.ctc?.fixedGross))}</div></div>
          </div>
          <div class="section-wrap">
            <div><div class="section-title">Fixed Salary</div><table><thead><tr><th>Particulars</th><th>Amount</th></tr></thead><tbody>${fixedSalaryRows}</tbody></table></div>
            <div><div class="section-title">Earnings</div><table><thead><tr><th>Particulars</th><th>Amount</th></tr></thead><tbody>${earningRows}</tbody></table></div>
            <div><div class="section-title">Deductions</div><table><thead><tr><th>Particulars</th><th>Amount</th></tr></thead><tbody>${deductionRows}</tbody></table></div>
          </div>
          <div class="summary">
            <div class="summary-box"><div class="summary-label">Payable Before PF Reim.</div><div class="summary-value">${escapeHtml(formatCurrency(payslip?.payable?.payableBeforePFReim))}</div></div>
            <div class="summary-box"><div class="summary-label">Net Salary</div><div class="summary-value">${escapeHtml(formatCurrency(payslip?.payable?.netSalary))}</div></div>
            <div class="summary-box"><div class="summary-label">Old Pending</div><div class="summary-value">${escapeHtml(formatCurrency(payslip?.payable?.oldPending))}</div></div>
            <div class="summary-box dark"><div class="summary-label">Final Payable</div><div class="summary-value">${escapeHtml(formatCurrency(payslip?.payable?.finalPayable))}</div></div>
          </div>
          <div class="note">This is a system-generated salary slip by ${escapeHtml(COMPANY_NAME)}.</div>
        </div>
        <script>window.onload=function(){window.print();};</script>
      </body>
    </html>
  `;
};

const PayslipSection = ({ title, rows, dark = false }) => (
  <Card sx={{ boxShadow: "none", border: "1px solid #dce6f4", borderRadius: "14px" }}>
    <MDBox px={2} py={1.5} sx={{ backgroundColor: dark ? "#0f172a" : "#1976d2" }}>
      <MDTypography variant="button" fontWeight="bold" color="white">
        {title}
      </MDTypography>
    </MDBox>
    <MDBox px={2} py={1}>
      {rows.map(([label, value]) => (
        <MDBox
          key={label}
          py={1}
          display="flex"
          justifyContent="space-between"
          borderBottom="1px solid #edf2f7"
        >
          <MDTypography variant="button" color="text">
            {label}
          </MDTypography>
          <MDTypography variant="button" fontWeight="medium">
            {formatCurrency(value)}
          </MDTypography>
        </MDBox>
      ))}
    </MDBox>
  </Card>
);

const InfoPanel = ({ title, rows }) => (
  <MDBox
    p={2}
    sx={{
      border: "1px solid #dde7f5",
      borderRadius: "14px",
      backgroundColor: "#f8fbff",
      height: "100%",
    }}
  >
    <MDTypography variant="caption" color="text" textTransform="uppercase" letterSpacing="0.9px">
      {title}
    </MDTypography>
    <MDBox mt={1.5}>
      {rows.map(([label, value]) => (
        <MDBox key={label} display="grid" gridTemplateColumns="120px 1fr" gap={1} mb={1}>
          <MDTypography variant="button" color="text">
            {label}
          </MDTypography>
          <MDTypography variant="button" fontWeight="bold">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ))}
    </MDBox>
  </MDBox>
);

const CtcBox = ({ label, value }) => (
  <MDBox
    p={2}
    sx={{
      border: "1px solid #d8e8ff",
      borderRadius: "14px",
      backgroundColor: "#eef6ff",
    }}
  >
    <MDTypography variant="caption" color="text" textTransform="uppercase" letterSpacing="0.8px">
      {label}
    </MDTypography>
    <MDTypography variant="h6" fontWeight="bold" color="dark">
      {formatCurrency(value)}
    </MDTypography>
  </MDBox>
);

const PayslipPreview = ({ payslip }) => (
  <MDBox
    sx={{
      background: "linear-gradient(180deg, #eef5ff 0%, #ffffff 28%)",
      border: "1px solid #d8e6fb",
      borderRadius: "16px",
      overflow: "hidden",
    }}
  >
    <MDBox
      px={3}
      py={3}
      sx={{
        borderBottom: "3px solid #1976d2",
        background: "linear-gradient(135deg, rgba(25,118,210,0.10) 0%, rgba(255,255,255,0.95) 65%)",
      }}
    >
      <MDTypography variant="h4" fontWeight="bold" color="dark">
        {COMPANY_NAME}
      </MDTypography>
      <MDTypography variant="button" color="text" textTransform="uppercase" letterSpacing="1px">
        Salary Slip for {payslip?.period?.label || "-"}
      </MDTypography>
    </MDBox>

    <MDBox p={3}>
      <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1.2fr 0.9fr" }} gap={2} mb={2.5}>
        <InfoPanel
          title="Employee Details"
          rows={[
            ["Name", payslip?.employee?.name],
            ["Department", payslip?.employee?.department],
            ["Designation", payslip?.employee?.designation],
            ["Status", payslip?.employee?.status],
          ]}
        />
        <InfoPanel
          title="Period Details"
          rows={[
            ["Month", payslip?.period?.month],
            ["Year", payslip?.period?.year],
            ["Payslip Period", payslip?.period?.label],
            ["Employee ID", payslip?.employee?.serialNo],
            ["Date of Joining", formatDate(payslip?.employee?.dateOfJoining)],
          ]}
        />
      </MDBox>

      <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={1.5} mb={3}>
        <CtcBox label="Fixed CTC" value={payslip?.ctc?.fixedCTC} />
        <CtcBox label="Incentive" value={payslip?.ctc?.incentive} />
        <CtcBox label="Total CTC" value={payslip?.ctc?.totalCTC} />
        <CtcBox label="Fixed Gross" value={payslip?.ctc?.fixedGross} />
      </MDBox>

      <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr 1fr" }} gap={2}>
        <PayslipSection title="Fixed Salary" rows={getFixedSalaryRows(payslip)} />
        <PayslipSection title="Earnings" rows={getEarningRows(payslip)} />
        <PayslipSection title="Deductions" rows={getDeductionRows(payslip)} dark />
      </MDBox>

      <Divider sx={{ my: 3 }} />

      <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={1.5}>
        {[
          ["Payable Before PF Reim.", payslip?.payable?.payableBeforePFReim, false],
          ["Net Salary", payslip?.payable?.netSalary, false],
          ["Old Pending", payslip?.payable?.oldPending, false],
          ["Final Payable", payslip?.payable?.finalPayable, true],
        ].map(([label, value, highlight]) => (
          <MDBox
            key={label}
            p={2}
            sx={{
              borderRadius: "14px",
              background: highlight ? "#0f172a" : "#eef6ff",
              color: highlight ? "#ffffff" : "#0f172a",
              border: highlight ? "none" : "1px solid #d8e8ff",
            }}
          >
            <MDTypography
              variant="caption"
              color={highlight ? "white" : "text"}
              textTransform="uppercase"
              letterSpacing="0.8px"
            >
              {label}
            </MDTypography>
            <MDTypography variant="h6" fontWeight="bold" color={highlight ? "white" : "dark"}>
              {formatCurrency(value)}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  </MDBox>
);

function Tables() {
  const [file, setFile] = useState(null);
  const [month1, setMonth1] = useState("");
  const [year1, setYear1] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  const tableRef = useRef();

  const { columns, rows, totalCount, pagecount, setPage, refresh, month, year, setMonth, setYear } =
    usePayslipTableData(setSelectedPayslip);

  const uploadSalarySheet = () => {
    if (!file || !month1 || !year1) {
      toast.error("Please select file, month and year");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setUploading(true);
        const base64 = reader.result.split(",")[1];

        await axiosInstance.post("/hrportal/upload", {
          fileBase64: base64,
          month: month1,
          year: Number(year1),
        });

        toast.success("Salary sheet uploaded successfully");
        setFile(null);
        refresh();
      } catch (err) {
        toast.error("Upload failed");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleDownloadPayslip = () => {
    if (!selectedPayslip) return;

    const printWindow = window.open("", "_blank", "width=1024,height=900");
    if (!printWindow) {
      toast.error("Please allow popups to download the payslip");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildPrintMarkup(selectedPayslip));
    printWindow.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  HR Payslip Management
                </MDTypography>
              </MDBox>

              <MDBox p={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} />

                <TextField
                  select
                  label="Month"
                  size="small"
                  value={month1}
                  onChange={(e) => setMonth1(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  {months.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Year"
                  size="small"
                  type="number"
                  value={year1}
                  onChange={(e) => setYear1(e.target.value)}
                  sx={{ width: 120 }}
                />

                <MDButton variant="gradient" color="success" disabled={uploading} onClick={uploadSalarySheet}>
                  {uploading ? "Uploading..." : "Upload Salary Sheet"}
                </MDButton>
              </MDBox>

              <MDBox px={3} pb={1} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  select
                  label="Filter Month"
                  size="small"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All Months</MenuItem>
                  {months.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Filter Year"
                  size="small"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  sx={{ width: 140 }}
                />

                <MDButton variant="outlined" color="info" onClick={refresh}>
                  Refresh
                </MDButton>
              </MDBox>

              <MDBox pt={3} ref={tableRef}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  showTotalEntries={false}
                  pageSize={pagecount}
                  totalCount={totalCount}
                  setPage={setPage}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={Boolean(selectedPayslip)}
        onClose={() => setSelectedPayslip(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            backgroundColor: "#f6f9ff",
          },
        }}
      >
        <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
          {selectedPayslip && <PayslipPreview payslip={selectedPayslip} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={() => setSelectedPayslip(null)}>
            Close
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleDownloadPayslip}>
            Download PDF
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Tables;
